// Phase 707 — Public review page for any document approval request.
// Reached at /review/:token. NO authentication; the token is the
// cryptographic capability.

import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  } catch { return iso; }
};

export default function PublicApprovalReview() {
  const { token } = useParams();
  const [appr, setAppr] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null); // 'accepted' | 'declined' | 'review_requested'

  // Decision state
  const [mode, setMode] = useState(null); // 'accept' | 'decline' | 'request'
  const [comment, setComment] = useState('');
  const [notes, setNotes] = useState('');
  // Signature
  const [sigMethod, setSigMethod] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const sigRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/approvals/${token}`);
        if (!cancelled) setAppr(res.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || 'Unable to load this approval link.');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    if (!mode) return;
    if (mode === 'decline' && !comment.trim()) {
      alert('Please provide a reason for declining.');
      return;
    }
    let signature_data = '';
    if (mode === 'accept') {
      if (sigMethod === 'draw') {
        if (!sigRef.current || sigRef.current.isEmpty()) {
          alert('Please draw your signature.');
          return;
        }
        signature_data = sigRef.current.toDataURL('image/png');
      } else if (!typedName.trim()) {
        alert('Please type your full name as a signature.');
        return;
      }
    }
    setBusy(true);
    try {
      const decision = mode === 'request' ? 'request_changes' : mode;
      const body = {
        decision,
        signature_method: mode === 'accept' ? sigMethod : '',
        signature_data: mode === 'accept' && sigMethod === 'draw' ? signature_data : '',
        signature_text: mode === 'accept' && sigMethod === 'type' ? typedName : '',
        comment, notes,
      };
      const res = await axios.post(`${API}/public/approvals/${token}/decision`, body);
      setSuccess(res.data?.status);
    } catch (e) {
      alert(e.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (err) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f7f9', fontFamily: 'Helvetica, Arial, sans-serif' }}>
      <div style={{ maxWidth: 480, background: '#fff', padding: 32, border: '1px solid #e5e7eb', textAlign: 'center' }}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <h2 style={{ color: '#1a1a2e' }}>Link unavailable</h2>
        <p style={{ color: '#666' }}>{err}</p>
      </div>
    </div>
  );
  if (!appr) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading…</div>;

  if (success) {
    const label = {
      accepted: { icon: '✅', text: 'Document accepted', color: '#0f7b3d' },
      declined: { icon: '❌', text: 'Document declined', color: '#9b1c1c' },
      review_requested: { icon: '💬', text: 'Review requested', color: '#b45309' },
    }[success] || { icon: '✅', text: 'Submitted', color: '#1a1a2e' };
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f7f9', fontFamily: 'Helvetica, Arial, sans-serif' }}>
        <div style={{ maxWidth: 520, background: '#fff', padding: 40, border: '1px solid #e5e7eb', textAlign: 'center' }}>
          <div style={{ fontSize: 64 }}>{label.icon}</div>
          <h2 style={{ color: label.color, margin: '12px 0' }}>{label.text}</h2>
          <p style={{ color: '#666' }}>Thank you. Your response has been recorded and the building administrator has been notified.</p>
          <p style={{ color: '#888', fontSize: 13, marginTop: 24 }}><b>{appr.document_label}</b><br />Version {appr.version}</p>
        </div>
      </div>
    );
  }

  // Phase 708b — Read-only mode for already-decided approvals. The
  // recipient can still re-open the link to view the signed PDF + audit
  // information, but cannot act again.
  const isReadOnly = ['accepted', 'declined', 'review_requested', 'cancelled', 'expired', 'invalidated', 'superseded'].includes(appr.status);
  const pdfUrl = `${API}/public/approvals/${token}/pdf`;
  if (isReadOnly) {
    const cfg = {
      accepted: { icon: '✅', label: 'Approved', tint: '#0f7b3d' },
      declined: { icon: '❌', label: 'Declined', tint: '#9b1c1c' },
      review_requested: { icon: '💬', label: 'Review Requested', tint: '#b45309' },
      cancelled: { icon: '🚫', label: 'Cancelled', tint: '#6b7280' },
      expired: { icon: '⏰', label: 'Expired', tint: '#6b7280' },
      invalidated: { icon: '⚠', label: 'Invalidated', tint: '#c2410c' },
      superseded: { icon: '⚠', label: 'Superseded', tint: '#c2410c' },
    }[appr.status] || { icon: '•', label: appr.status, tint: '#1a1a2e' };
    return (
      <div style={{ minHeight: '100vh', background: '#f7f7f9', fontFamily: 'Helvetica, Arial, sans-serif', color: '#1f1f1f' }}>
        <header style={{ background: cfg.tint, color: '#fff', padding: '16px 24px' }}>
          <div style={{ maxWidth: 1200, margin: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 12, opacity: 0.7, letterSpacing: 1.5 }}>{cfg.icon} {cfg.label.toUpperCase()} — READ ONLY</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{appr.document_label}</div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>Version {appr.version}</div>
            </div>
            <a href={pdfUrl} download={appr.pdf_filename || 'document.pdf'} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', padding: '8px 14px', textDecoration: 'none', fontSize: 13 }} data-testid="approval-download-btn">Download Signed PDF</a>
          </div>
        </header>
        <div style={{ maxWidth: 1200, margin: 'auto', padding: 24, display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(280px,1fr)', gap: 24 }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', minHeight: 720 }}>
            <iframe title="Document" src={pdfUrl} style={{ width: '100%', height: '85vh', border: 0 }} data-testid="approval-pdf-iframe-readonly" />
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', padding: 20 }} data-testid="approval-readonly-panel">
            <h3 style={{ fontSize: 15, margin: '0 0 16px', color: cfg.tint }}>{cfg.icon} {cfg.label}</h3>
            {appr.signed_at && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: '#888', letterSpacing: 1, textTransform: 'uppercase' }}>Signed Date</div>
                <div style={{ fontSize: 13, fontFamily: 'monospace' }}>{fmtDate(appr.signed_at)}</div>
              </div>
            )}
            {appr.signature_method === 'draw' && appr.signature_data && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: '#888', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Electronic Signature</div>
                <img src={appr.signature_data} alt="Signature" style={{ maxHeight: 90, border: '1px solid #e5e7eb', background: '#fff' }} />
              </div>
            )}
            {appr.signature_method === 'type' && appr.signature_text && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: '#888', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Electronic Signature</div>
                <div style={{ fontSize: 22, fontStyle: 'italic', fontFamily: 'serif', color: '#0f7b3d', padding: '8px 12px', border: '1px solid #e5e7eb', background: '#fff' }}>{appr.signature_text}</div>
              </div>
            )}
            {appr.decision_comment && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: '#888', letterSpacing: 1, textTransform: 'uppercase' }}>
                  {appr.status === 'declined' ? 'Decline Reason' : appr.status === 'review_requested' ? 'Review Request' : 'Comments'}
                </div>
                <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{appr.decision_comment}</div>
              </div>
            )}
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0', fontSize: 11, color: '#888', fontFamily: 'monospace' }}>
              <div>PDF SHA-256: {(appr.pdf_hash || '').slice(0, 24)}…</div>
              <div style={{ marginTop: 4 }}>Token: {(appr.token || '').slice(0, 12)}…</div>
            </div>
            <p style={{ fontSize: 11, color: '#888', marginTop: 16, lineHeight: 1.5 }}>
              No further modifications are possible. This document is permanently archived in the audit trail.
            </p>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div style={{ minHeight: '100vh', background: '#f7f7f9', fontFamily: 'Helvetica, Arial, sans-serif', color: '#1f1f1f' }}>
      {/* Header */}
      <header style={{ background: '#1a1a2e', color: '#fff', padding: '16px 24px' }}>
        <div style={{ maxWidth: 1200, margin: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.7, letterSpacing: 1.5 }}>DOCUMENT FOR APPROVAL</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{appr.document_label}</div>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>Version {appr.version} • Expires {fmtDate(appr.token_expires_at)}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <a href={pdfUrl} download={appr.pdf_filename || 'document.pdf'} style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '8px 14px', textDecoration: 'none', fontSize: 13 }} data-testid="approval-download-btn">Download PDF</a>
            <button onClick={() => window.print()} style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '8px 14px', border: 'none', fontSize: 13, cursor: 'pointer' }} data-testid="approval-print-btn">Print</button>
          </div>
        </div>
      </header>

      {/* Body */}
      <div style={{ maxWidth: 1200, margin: 'auto', padding: 24, display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(280px,1fr)', gap: 24 }}>
        {/* PDF viewer */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', minHeight: 720 }}>
          <iframe title="Document" src={pdfUrl} style={{ width: '100%', height: '85vh', border: 0 }} data-testid="approval-pdf-iframe" />
        </div>

        {/* Action panel */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', padding: 20 }}>
          <h3 style={{ fontSize: 15, margin: '0 0 16px', color: '#1a1a2e' }}>Your Decision</h3>

          {!mode && (
            <div style={{ display: 'grid', gap: 10 }}>
              <button onClick={() => setMode('accept')} style={btn('#0f7b3d')} data-testid="approval-accept-btn">✓ Accept</button>
              <button onClick={() => setMode('decline')} style={btn('#9b1c1c')} data-testid="approval-decline-btn">✕ Decline</button>
              <button onClick={() => setMode('request')} style={btn('#b45309')} data-testid="approval-request-btn">💬 Request Changes</button>
              <p style={{ fontSize: 11, color: '#888', marginTop: 8 }}>Your decision will be recorded with a full audit trail (timestamp, IP address, browser).</p>
            </div>
          )}

          {mode === 'accept' && (
            <div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 600, color: '#0f7b3d', marginBottom: 8 }}>✓ Accepting — please sign:</div>
                <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                  <button onClick={() => setSigMethod('draw')} style={tab(sigMethod === 'draw')} data-testid="sig-draw-tab">Draw</button>
                  <button onClick={() => setSigMethod('type')} style={tab(sigMethod === 'type')} data-testid="sig-type-tab">Type</button>
                </div>
                {sigMethod === 'draw' ? (
                  <div>
                    <div style={{ border: '1px solid #ddd', background: '#fafafa' }}>
                      <SignatureCanvas ref={sigRef} penColor="#1a1a2e" canvasProps={{ width: 320, height: 140, 'data-testid': 'sig-canvas' }} />
                    </div>
                    <button onClick={() => sigRef.current?.clear()} style={{ fontSize: 11, color: '#888', background: 'none', border: 0, cursor: 'pointer', padding: '4px 0' }} data-testid="sig-clear-btn">Clear</button>
                  </div>
                ) : (
                  <input type="text" value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Type your full name" style={{ width: '100%', padding: 8, border: '1px solid #ddd', fontFamily: 'cursive', fontSize: 22 }} data-testid="sig-typed-input" />
                )}
              </div>
              <label style={lbl}>Additional Notes (optional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={ta} data-testid="approval-notes-input" />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={() => setMode(null)} style={btnSec()}>Back</button>
                <button onClick={submit} disabled={busy} style={btn('#0f7b3d', busy)} data-testid="approval-submit-btn">{busy ? 'Submitting…' : 'Submit Acceptance'}</button>
              </div>
            </div>
          )}

          {mode === 'decline' && (
            <div>
              <div style={{ fontWeight: 600, color: '#9b1c1c', marginBottom: 8 }}>✕ Declining</div>
              <label style={lbl}>Reason for declining *</label>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} style={ta} placeholder="Please explain why you are declining…" data-testid="approval-comment-input" />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={() => setMode(null)} style={btnSec()}>Back</button>
                <button onClick={submit} disabled={busy} style={btn('#9b1c1c', busy)} data-testid="approval-submit-btn">{busy ? 'Submitting…' : 'Submit Decline'}</button>
              </div>
            </div>
          )}

          {mode === 'request' && (
            <div>
              <div style={{ fontWeight: 600, color: '#b45309', marginBottom: 8 }}>💬 Requesting changes</div>
              <label style={lbl}>Comments *</label>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} style={ta} placeholder="What changes would you like to see?" data-testid="approval-comment-input" />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={() => setMode(null)} style={btnSec()}>Back</button>
                <button onClick={submit} disabled={busy || !comment.trim()} style={btn('#b45309', busy || !comment.trim())} data-testid="approval-submit-btn">{busy ? 'Submitting…' : 'Submit'}</button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #eee', fontSize: 11, color: '#888' }}>
            <div>Recipient: <b>{appr.owner_name || appr.owner_email}</b></div>
            <div>Email: {appr.owner_email}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const btn = (color, disabled = false) => ({
  background: disabled ? '#aaa' : color, color: '#fff', border: 'none',
  padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: disabled ? 'wait' : 'pointer',
  width: '100%',
});
const btnSec = () => ({
  background: '#fff', color: '#666', border: '1px solid #ddd',
  padding: '10px 16px', fontSize: 13, cursor: 'pointer', width: '100%',
});
const tab = (active) => ({
  flex: 1, background: active ? '#1a1a2e' : '#f3f4f6', color: active ? '#fff' : '#666',
  border: 0, padding: '6px 10px', fontSize: 12, cursor: 'pointer',
});
const lbl = { fontSize: 12, color: '#666', display: 'block', marginTop: 8, marginBottom: 4 };
const ta = { width: '100%', border: '1px solid #ddd', padding: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' };
